import { useState, useEffect, useCallback, useRef } from 'react';
import { Modal, ModalBody, Spinner } from 'reactstrap';
import Select from 'react-select';
import FeatherIcon from 'feather-icons-react';
import { useDropzone } from 'react-dropzone';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import {
  requestSuiteInspectionUploadUrl,
  confirmSuiteInspectionUpload,
  getSuiteInspectionAttachmentSignedUrls,
} from './FormikHelpers/Api';

const CLEANLINESS_OPTIONS = [
  { value: 'clean', label: 'Clean', icon: 'check-circle', color: '#10B981', bg: '#D1FAE5' },
  { value: 'okay',  label: 'Okay',  icon: 'thumbs-up',    color: '#F59E0B', bg: '#FEF3C7' },
  { value: 'dirty', label: 'Dirty', icon: 'alert-triangle', color: '#EF4444', bg: '#FEE2E2' },

];

const PLUMBING_OPTIONS = [
  { value: 'no_issue',     label: 'No Issue',     icon: 'check-circle',   color: '#10B981', bg: '#D1FAE5' },
  { value: 'needs_repair', label: 'Needs Repair',  icon: 'alert-triangle', color: '#EF4444', bg: '#FEE2E2' },
];

const DYE_OPTIONS = [
  { value: 'passed', label: 'Passed', icon: 'check', color: '#10B981', bg: '#D1FAE5' },
  { value: 'failed', label: 'Failed', icon: 'x',     color: '#EF4444', bg: '#FEE2E2' },
];

const BOLT_OPTIONS = [
  { value: 'no_issue',     label: 'No Issue',    icon: 'check',         color: '#10B981', bg: '#D1FAE5' },
  { value: 'needs_repair', label: 'Needs Repair', icon: 'alert-circle', color: '#EF4444', bg: '#FEE2E2' },
];

const PEOPLE_OPTIONS = ['1', '2', '3', '4', '5', '6+'].map(v => ({ value: v, label: v }));

const YES_NO_OPTIONS = [
  { value: true,  label: 'Yes', icon: 'alert-circle', color: '#EF4444', bg: '#FEE2E2' },
  { value: false, label: 'No',  icon: 'check',        color: '#10B981', bg: '#D1FAE5' },
];

const YES_NO_ONLY_OPTIONS = [
  { value: 'yes', label: 'Yes', icon: 'check', color: '#10B981', bg: '#D1FAE5' },
  { value: 'no',  label: 'No',  icon: 'x',     color: '#EF4444', bg: '#FEE2E2' },
];

const YES_NO_STR_OPTIONS = [
  { value: 'yes', label: 'Yes', icon: 'alert-circle', color: '#EF4444', bg: '#FEE2E2' },
  { value: 'no',  label: 'No',  icon: 'check',        color: '#10B981', bg: '#D1FAE5' },
];

const HRV_OPTIONS = [
  { value: 'reduce', label: 'Reduce', icon: 'arrow-down', color: '#F59E0B', bg: '#FEF3C7' },
  { value: 'normal', label: 'Normal', icon: 'minus',      color: '#10B981', bg: '#D1FAE5' },
  { value: 'off',    label: 'Off',    icon: 'power',      color: '#6B7280', bg: '#F3F4F6' },
];

const FURNACE_OPTIONS = [
  { value: 'clean',    label: 'Clean',    icon: 'check',      color: '#10B981', bg: '#D1FAE5' },
  { value: 'replaced', label: 'Replaced', icon: 'refresh-cw', color: '#F59E0B', bg: '#FEF3C7' },
];

const STATUS_OPTIONS = [
  { value: 'passed',     label: 'Passed',     icon: 'check-circle', color: '#10B981', bg: '#D1FAE5' },
  { value: 'has issues', label: 'Has Issues', icon: 'alert-circle', color: '#EF4444', bg: '#FEE2E2' },
];

const ALLOWED_TYPES = 'image/jpeg,image/png,image/gif,image/webp,video/mp4,video/quicktime,video/webm,video/x-msvideo,video/x-matroska';
const MAX_FILE_SIZE = 120 * 1024 * 1024;
const MAX_FILES = 10;

const EMPTY_FORM = (buildingType) => ({
  unit_number: '',
  inspection_date: new Date().toISOString().split('T')[0],
  cleanliness: '',
  any_damages: false,
  number_of_dogs: 0,
  number_of_cats: 0,
  plumbing: '',
  dye_test: '',
  lubricate_dead_bolt: '',
  number_of_people: '',
  signs_of_smoking: false,
  comments: '',
  overall_status: '',
  ...(buildingType === 'Townhouse'
    ? { hrv_setting: '', furnace_filter: '', flooring_damage: '' }
    : { closed_window: '', furniture_away: '' }),
  attachments: [],
  is_draft: false,
});

const selectStyles = (hasError) => ({
  menuPortal: b => ({ ...b, zIndex: 99999 }),
  container: b => ({ ...b, width: '100%' }),
  control: (b, { isFocused }) => ({
    ...b,
    minHeight: 40,
    fontSize: '0.82rem',
    borderRadius: 10,
    borderColor: hasError ? '#EF4444' : isFocused ? '#6366F1' : '#E2E8F0',
    borderWidth: '1.5px',
    boxShadow: isFocused ? `0 0 0 3px ${hasError ? '#FEE2E2' : 'rgba(99,102,241,0.12)'}` : 'none',
    '&:hover': { borderColor: hasError ? '#EF4444' : '#6366F1' },
    backgroundColor: '#FAFBFF',
    cursor: 'pointer',
  }),
  valueContainer: b => ({ ...b, padding: '0 12px' }),
  dropdownIndicator: b => ({ ...b, padding: '0 8px', color: '#94A3B8' }),
  indicatorSeparator: () => ({ display: 'none' }),
  menu: b => ({ ...b, fontSize: '0.82rem', borderRadius: 10, zIndex: 9999, boxShadow: '0 12px 40px rgba(15,28,46,0.15)', border: '1px solid #E2E8F0' }),
  option: (b, { isSelected, isFocused }) => ({
    ...b,
    backgroundColor: isSelected ? '#6366F1' : isFocused ? '#EEF2FF' : 'white',
    color: isSelected ? 'white' : '#0F172A',
    fontWeight: isSelected ? 600 : 400,
    padding: '8px 14px',
    borderRadius: 6,
    margin: '1px 3px',
    width: 'calc(100% - 6px)',
  }),
  singleValue: b => ({ ...b, color: '#0F172A', fontWeight: 600 }),
  placeholder: b => ({ ...b, color: '#CBD5E1', fontSize: '0.82rem' }),
});

const FieldLabel = ({ children, required }) => (
  <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, color: '#64748B', letterSpacing: '0.6px', textTransform: 'uppercase', marginBottom: 6 }}>
    {children}{required && <span style={{ color: '#EF4444', marginLeft: 3 }}>*</span>}
  </label>
);

const ErrorMsg = ({ msg }) =>
  msg ? <div style={{ fontSize: '10px', color: '#EF4444', marginTop: 4, fontWeight: 500 }}>{msg}</div> : null;

const TextInput = ({ value, onChange, placeholder, type = 'text', hasError }) => (
  <input
    type={type}
    value={value || ''}
    onChange={e => onChange(e.target.value)}
    placeholder={placeholder}
    max={type === 'date' ? new Date().toISOString().split('T')[0] : undefined}
    style={{
      width: '100%',
      height: 40,
      borderRadius: 10,
      border: `1.5px solid ${hasError ? '#EF4444' : '#E2E8F0'}`,
      padding: '0 12px',
      fontSize: '0.82rem',
      color: '#0F172A',
      background: '#FAFBFF',
      outline: 'none',
      fontFamily: 'inherit',
      transition: 'border-color 0.15s, box-shadow 0.15s',
    }}
    onFocus={e => {
      e.target.style.borderColor = hasError ? '#EF4444' : '#6366F1';
      e.target.style.boxShadow = `0 0 0 3px ${hasError ? '#FEE2E2' : 'rgba(99,102,241,0.12)'}`;
    }}
    onBlur={e => {
      e.target.style.borderColor = hasError ? '#EF4444' : '#E2E8F0';
      e.target.style.boxShadow = 'none';
    }}
  />
);

const NumberStepper = ({ value, onChange, label, icon, color = '#6366F1', min = 0, max = 20 }) => {
  const num = parseInt(value, 10) || 0;
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8,
      background: '#FAFBFF', border: '1.5px solid #E2E8F0',
      borderRadius: 10, padding: '6px 10px',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 5, flex: 1 }}>
        <span style={{ fontSize: '16px' }}>{icon}</span>
        <span style={{ fontSize: '12px', fontWeight: 600, color: '#475569' }}>{label}</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <button
          type="button"
          onClick={() => onChange(Math.max(min, num - 1))}
          style={{
            width: 26, height: 26, borderRadius: 8, border: `1.5px solid ${color}20`,
            background: `${color}10`, color, fontWeight: 700, fontSize: '14px',
            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >−</button>
        <span style={{ minWidth: 22, textAlign: 'center', fontWeight: 800, fontSize: '15px', color: num > 0 ? color : '#94A3B8' }}>{num}</span>
        <button
          type="button"
          onClick={() => onChange(Math.min(max, num + 1))}
          style={{
            width: 26, height: 26, borderRadius: 8, border: `1.5px solid ${color}20`,
            background: `${color}10`, color, fontWeight: 700, fontSize: '14px',
            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >+</button>
      </div>
    </div>
  );
};

const TextareaInput = ({ value, onChange, placeholder }) => (
  <textarea
    value={value || ''}
    onChange={e => onChange(e.target.value)}
    placeholder={placeholder}
    rows={3}
    style={{
      width: '100%',
      borderRadius: 10,
      border: '1.5px solid #E2E8F0',
      padding: '10px 12px',
      fontSize: '0.82rem',
      color: '#0F172A',
      background: '#FAFBFF',
      outline: 'none',
      fontFamily: 'inherit',
      resize: 'vertical',
      transition: 'border-color 0.15s, box-shadow 0.15s',
    }}
    onFocus={e => { e.target.style.borderColor = '#6366F1'; e.target.style.boxShadow = '0 0 0 3px rgba(99,102,241,0.12)'; }}
    onBlur={e => { e.target.style.borderColor = '#E2E8F0'; e.target.style.boxShadow = 'none'; }}
  />
);

const PillToggle = ({ options, value, onChange }) => (
  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
    {options.map(opt => {
      const active = String(opt.value) === String(value);
      return (
        <button
          key={String(opt.value)}
          type="button"
          onClick={() => onChange(opt.value)}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            padding: '6px 14px', borderRadius: 20,
            border: `1.5px solid ${active ? opt.color : '#E2E8F0'}`,
            background: active ? opt.bg : '#FAFBFF',
            color: active ? opt.color : '#94A3B8',
            fontSize: '12px', fontWeight: active ? 700 : 500,
            cursor: 'pointer', transition: 'all 0.15s ease',
          }}
        >
          <FeatherIcon icon={opt.icon} size={11} color={active ? opt.color : '#94A3B8'} />
          {opt.label}
        </button>
      );
    })}
  </div>
);

const SectionHeader = ({ icon, title, accent = '#6366F1', badge }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, paddingBottom: 10, borderBottom: '2px solid #F1F5F9' }}>
    <div style={{ width: 30, height: 30, borderRadius: 8, background: `${accent}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <FeatherIcon icon={icon} size={15} color={accent} />
    </div>
    <span style={{ fontSize: '13px', fontWeight: 800, color: '#0F172A', letterSpacing: '-0.2px' }}>{title}</span>
    {badge && <span style={{ marginLeft: 'auto', fontSize: '10px', fontWeight: 700, padding: '2px 9px', borderRadius: 20, background: '#FEF3C7', color: '#D97706', border: '1px solid #FCD34D' }}>{badge}</span>}
  </div>
);

const Grid = ({ children, cols = 2 }) => (
  <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: '14px 20px' }}>
    {children}
  </div>
);

const Field = ({ children, span = 1 }) => (
  <div style={{ gridColumn: `span ${span}` }}>{children}</div>
);



const AttachmentZone = ({ pendingFiles, onPendingChange, existingKeys, onExistingChange, inspectionId, isEdit }) => {
  const [signedUrls, setSignedUrls] = useState({});
  const fetchedRef = useRef(new Set());
  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);

  useEffect(() => {
    const missing = existingKeys.filter(k => !signedUrls[k] && !fetchedRef.current.has(k));
    if (!missing.length) return;
    missing.forEach(k => fetchedRef.current.add(k));
    getSuiteInspectionAttachmentSignedUrls(missing)
      .then(urls => setSignedUrls(prev => ({ ...prev, ...urls })))
      .catch(() => toast.error('Some previews failed to load'));
  }, [existingKeys]);

  const addFiles = (accepted, rejected) => {
    rejected.forEach(({ file, errors }) => errors.forEach(e => {
      if (e.code === 'file-too-large') toast.error(`${file.name} exceeds 120 MB`);
      if (e.code === 'file-invalid-type') toast.error(`${file.name}: unsupported type`);
    }));
    const rem = MAX_FILES - (pendingFiles.length + existingKeys.length);
    const toAdd = accepted.slice(0, rem);
    if (accepted.length > rem) toast.warn(`Max ${MAX_FILES} files`);
    if (!toAdd.length) return;
    onPendingChange([...pendingFiles, ...toAdd.map(f => ({
      file: f, name: f.name, type: f.type,
      preview: f.type.startsWith('image/') ? URL.createObjectURL(f) : null,
    }))]);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: addFiles,
    accept: ALLOWED_TYPES,
    multiple: true,
    maxSize: MAX_FILE_SIZE,
    noClick: true, 
  });

  const handleGalleryChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const valid = [], rejected = [];
    files.forEach(f => {
      if (f.size > MAX_FILE_SIZE) rejected.push({ file: f, errors: [{ code: 'file-too-large' }] });
      else valid.push(f);
    });
    addFiles(valid, rejected);
    e.target.value = ''; 
  };

  const handleCameraChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const valid = [], rejected = [];
    files.forEach(f => {
      if (f.size > MAX_FILE_SIZE) rejected.push({ file: f, errors: [{ code: 'file-too-large' }] });
      else valid.push(f);
    });
    addFiles(valid, rejected);
    e.target.value = '';
  };

  useEffect(() => () => pendingFiles.forEach(f => f.preview && URL.revokeObjectURL(f.preview)), []);

  const removePending = (i) => onPendingChange(pendingFiles.filter((_, idx) => idx !== i));
  const removeExisting = async (i) => {
    const r = await Swal.fire({
      title: 'Remove attachment?', icon: 'warning', showCancelButton: true,
      confirmButtonColor: '#EF4444', confirmButtonText: 'Remove', width: 340,
    });
    if (r.isConfirmed) onExistingChange(existingKeys.filter((_, idx) => idx !== i));
  };

  const total = pendingFiles.length + existingKeys.length;
  const atLimit = total >= MAX_FILES;

  return (
    <div>
      {/* Hidden inputs outside dropzone to avoid event conflicts */}
      <input
        ref={galleryInputRef}
        type="file"
        accept={ALLOWED_TYPES}
        multiple
        style={{ display: 'none' }}
        onChange={handleGalleryChange}
      />
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*,video/*"
        capture="environment"
        style={{ display: 'none' }}
        onChange={handleCameraChange}
      />

      <div
        style={{
          border: `2px dashed ${isDragActive ? '#6366F1' : '#CBD5E1'}`,
          borderRadius: 12,
          padding: '16px 20px',
          background: isDragActive ? '#EEF2FF' : '#FAFBFF',
          transition: 'all 0.2s',
          marginBottom: 12,
          position: 'relative'
        }}
      >
        {/* Drop zone layer */}
        <div 
          {...getRootProps()} 
          style={{ 
            position: 'absolute', inset: 0, zIndex: isDragActive ? 10 : 0,
            cursor: isDragActive ? 'copy' : 'default'
          }}
        >
          <input {...getInputProps()} />
        </div>

        {/* Content layer - keeps buttons interactive */}
        <div style={{ position: 'relative', zIndex: 5, pointerEvents: 'none' }}>
          <div style={{ textAlign: 'center', marginBottom: 12 }}>
            <FeatherIcon icon="upload-cloud" size={26} color={isDragActive ? '#6366F1' : '#CBD5E1'} />
            <div style={{ fontSize: '12px', color: '#94A3B8', marginTop: 6, fontWeight: 500 }}>
              {isDragActive ? 'Drop files here…' : `Drag & drop files here, or use the buttons below (${total}/${MAX_FILES})`}
            </div>
            <div style={{ fontSize: '10px', color: '#CBD5E1', marginTop: 3 }}>Images & videos up to 120 MB</div>
          </div>

          <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap', pointerEvents: 'auto' }}>
            <button
              type="button"
              disabled={atLimit}
              onClick={() => galleryInputRef.current?.click()}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '8px 16px', borderRadius: 10,
                border: `1.5px solid ${atLimit ? '#E2E8F0' : '#6366F1'}`,
                background: atLimit ? '#F8FAFC' : 'linear-gradient(135deg, #EEF2FF, #F5F3FF)',
                color: atLimit ? '#CBD5E1' : '#6366F1',
                fontSize: '12px', fontWeight: 700,
                cursor: atLimit ? 'not-allowed' : 'pointer',
                transition: 'all 0.15s',
              }}
              onMouseEnter={e => { if (!atLimit) { e.currentTarget.style.background = 'linear-gradient(135deg, #6366F1, #8B5CF6)'; e.currentTarget.style.color = 'white'; } }}
              onMouseLeave={e => { if (!atLimit) { e.currentTarget.style.background = 'linear-gradient(135deg, #EEF2FF, #F5F3FF)'; e.currentTarget.style.color = '#6366F1'; } }}
            >
              <FeatherIcon icon="image" size={13} />
              {/Mobi|Android|iPhone/i.test(navigator.userAgent) ? 'Gallery' : 'Upload Media'}
            </button>

            {/Mobi|Android|iPhone/i.test(navigator.userAgent) && (
              <button
                type="button"
                disabled={atLimit}
                onClick={() => cameraInputRef.current?.click()}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '8px 16px', borderRadius: 10,
                  border: `1.5px solid ${atLimit ? '#E2E8F0' : '#10B981'}`,
                  background: atLimit ? '#F8FAFC' : 'linear-gradient(135deg, #ECFDF5, #D1FAE5)',
                  color: atLimit ? '#CBD5E1' : '#059669',
                  fontSize: '12px', fontWeight: 700,
                  cursor: atLimit ? 'not-allowed' : 'pointer',
                  transition: 'all 0.15s',
                }}
                onMouseEnter={e => { if (!atLimit) { e.currentTarget.style.background = 'linear-gradient(135deg, #10B981, #059669)'; e.currentTarget.style.color = 'white'; } }}
                onMouseLeave={e => { if (!atLimit) { e.currentTarget.style.background = 'linear-gradient(135deg, #ECFDF5, #D1FAE5)'; e.currentTarget.style.color = '#059669'; } }}
              >
                <FeatherIcon icon="camera" size={13} />
                Take Photo / Video
              </button>
            )}
          </div>

          {atLimit && (
            <div style={{ textAlign: 'center', marginTop: 8, fontSize: '11px', color: '#EF4444', fontWeight: 600 }}>
              Maximum {MAX_FILES} files reached. Remove one to add more.
            </div>
          )}
        </div>
      </div>

      {/* Thumbnails */}
      {total > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {existingKeys.map((k, i) => {
            const url = signedUrls[k];
            const isVid = /\.(mp4|mov|webm|avi|mkv)$/i.test(k);
            return (
              <div key={i} style={{ position: 'relative', width: 60, height: 60, borderRadius: 10, overflow: 'hidden', border: '2px solid #E2E8F0', flexShrink: 0 }}>
                {url
                  ? isVid
                    ? <div style={{ width: '100%', height: '100%', background: '#1E293B', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><FeatherIcon icon="video" size={20} color="#94A3B8" /></div>
                    : <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  : <div style={{ width: '100%', height: '100%', background: '#F1F5F9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Spinner size="sm" /></div>
                }
                <button type="button" onClick={() => removeExisting(i)} style={{ position: 'absolute', top: 2, right: 2, width: 18, height: 18, borderRadius: '50%', background: '#EF4444', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: 0 }}>
                  <FeatherIcon icon="x" size={10} color="white" />
                </button>
              </div>
            );
          })}
          {pendingFiles.map((f, i) => (
            <div key={`p${i}`} style={{ position: 'relative', width: 60, height: 60, borderRadius: 10, overflow: 'hidden', border: '2px dashed #F59E0B', flexShrink: 0 }}>
              {f.type.startsWith('image/') && f.preview
                ? <img src={f.preview} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                : <div style={{ width: '100%', height: '100%', background: '#1E293B', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><FeatherIcon icon="video" size={16} color="#94A3B8" /></div>
              }
              <button type="button" onClick={() => removePending(i)} style={{ position: 'absolute', top: 2, right: 2, width: 18, height: 18, borderRadius: '50%', background: '#EF4444', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: 0 }}>
                <FeatherIcon icon="x" size={10} color="white" />
              </button>
            </div>
          ))}
        </div>
      )}

      {pendingFiles.length > 0 && (
        <div style={{ fontSize: '11px', color: '#F59E0B', marginTop: 8, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 5 }}>
          <FeatherIcon icon="clock" size={11} color="#F59E0B" />
          {pendingFiles.length} file(s) will upload after {isEdit ? 'saving' : 'creating'}
        </div>
      )}
    </div>
  );
};

const STEPS = ['Basic Info', 'Checks', 'Summary'];

const StepBar = ({ current, total, labels }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0, padding: '0 24px', marginBottom: 28 }}>
    {labels.map((label, i) => (
      <div key={i} style={{ display: 'flex', alignItems: 'center', flex: i < labels.length - 1 ? 1 : 'none' }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, flexShrink: 0 }}>
          <div style={{
            width: 32, height: 32, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: i < current ? '#6366F1' : i === current ? 'linear-gradient(135deg, #6366F1, #8B5CF6)' : '#F1F5F9',
            border: i === current ? '3px solid #C7D2FE' : i < current ? '2px solid #6366F1' : '2px solid #E2E8F0',
            transition: 'all 0.3s ease',
            boxShadow: i === current ? '0 0 0 4px rgba(99,102,241,0.15)' : 'none',
          }}>
            {i < current
              ? <FeatherIcon icon="check" size={14} color="white" />
              : <span style={{ fontSize: '12px', fontWeight: 800, color: i === current ? 'white' : '#94A3B8' }}>{i + 1}</span>
            }
          </div>
          <span style={{ fontSize: '10px', fontWeight: i === current ? 700 : 500, color: i === current ? '#6366F1' : i < current ? '#10B981' : '#94A3B8', whiteSpace: 'nowrap' }}>{label}</span>
        </div>
        {i < labels.length - 1 && (
          <div style={{ flex: 1, height: 2, background: i < current ? '#6366F1' : '#E2E8F0', margin: '0 8px', marginBottom: 18, borderRadius: 2, transition: 'background 0.3s ease' }} />
        )}
      </div>
    ))}
  </div>
);

const DraftBanner = () => (
  <div style={{
    display: 'flex', alignItems: 'center', gap: 10,
    background: 'linear-gradient(135deg, #FFFBEB, #FEF3C7)',
    border: '1.5px solid #FCD34D',
    borderRadius: 10, padding: '10px 14px', marginBottom: 16,
  }}>
    <FeatherIcon icon="edit-3" size={15} color="#D97706" />
    <div>
      <div style={{ fontSize: '12px', fontWeight: 700, color: '#92400E' }}>Draft Mode</div>
      <div style={{ fontSize: '11px', color: '#B45309' }}>This inspection is saved as a draft. Complete all required fields and submit to finalize.</div>
    </div>
  </div>
);

export const InspectionFormModal = ({
  isOpen,
  onClose,
  mode,
  initialData,
  buildingType,
  unitOptions,
  onSave,
}) => {
  const isApt = buildingType !== 'Townhouse';
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM(buildingType));
  const [errors, setErrors] = useState({});
  const [pendingFiles, setPendingFiles] = useState([]);
  const [existingKeys, setExistingKeys] = useState([]);

  const isDraftRecord = mode === 'edit' && initialData?.is_draft === true;

  useEffect(() => {
    if (isOpen) {
      setStep(0);
      setErrors({});
      setPendingFiles([]);
      if (mode === 'edit' && initialData) {
        setForm({ ...EMPTY_FORM(buildingType), ...initialData, attachments: undefined });
        setExistingKeys(initialData.attachments || []);
      } else {
        setForm(prev => ({
          ...EMPTY_FORM(buildingType),
          unit_number: initialData?.unit_number || '',
        }));
        setExistingKeys([]);
      }
    }
  }, [isOpen, mode, initialData, buildingType]);

  const set = useCallback((k, v) => {
    setForm(p => ({ ...p, [k]: v }));
    setErrors(p => ({ ...p, [k]: undefined }));
  }, []);

  const validateStep = (s) => {
    const e = {};
    if (s === 0) {
      if (!String(form.unit_number || '').trim()) e.unit_number = 'Required';
      if (!form.inspection_date) e.inspection_date = 'Required';
      if (!form.cleanliness) e.cleanliness = 'Required';
      if (!form.plumbing) e.plumbing = 'Required';
    }
    if (s === 1) {
      if (!form.dye_test) e.dye_test = 'Required';
      if (!form.lubricate_dead_bolt) e.lubricate_dead_bolt = 'Required';
      if (!form.number_of_people) e.number_of_people = 'Required';
      if (isApt) {
        if (!form.closed_window) e.closed_window = 'Required';
        if (!form.furniture_away) e.furniture_away = 'Required';
      } else {
        if (!form.hrv_setting) e.hrv_setting = 'Required';
        if (!form.furnace_filter) e.furnace_filter = 'Required';
        if (!form.flooring_damage) e.flooring_damage = 'Required';
      }
    }
    if (s === 2) {
      if (!form.overall_status) e.overall_status = 'Required';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const next = () => { if (validateStep(step)) setStep(s => s + 1); };
  const back = () => setStep(s => s - 1);

  const handleCancel = useCallback(async () => {
    const hasChanges = pendingFiles.length > 0 ||
      Object.keys(form).some(k => {
        const empty = EMPTY_FORM(buildingType);
        return form[k] !== empty[k];
      });

    if (!hasChanges) {
      onClose();
      return;
    }

    const result = await Swal.fire({
      title: 'Discard changes?',
      text: 'Any unsaved changes will be lost.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#EF4444',
      cancelButtonColor: '#94A3B8',
      confirmButtonText: 'Yes, discard',
      cancelButtonText: 'Keep editing',
      width: 380,
    });

    if (result.isConfirmed) {
      onClose();
    }
  }, [form, pendingFiles, buildingType, onClose]);

  const processAttachments = async (inspectionId) => {
    if (!pendingFiles.length) return;
    toast.info(`Uploading ${pendingFiles.length} attachment(s)...`);
    for (const pending of pendingFiles) {
      try {
        const uploadInfo = await requestSuiteInspectionUploadUrl(inspectionId, pending.name, pending.type);
        const uploadRes = await fetch(uploadInfo.presignedUrl, {
          method: 'PUT',
          body: pending.file,
          headers: { 'Content-Type': pending.type },
        });
        if (!uploadRes.ok) throw new Error(`Upload failed: ${pending.name}`);
        await confirmSuiteInspectionUpload(inspectionId, uploadInfo.key);
      } catch (err) {
        console.error('Attachment upload error:', err);
        toast.error(`Failed to upload ${pending.name}`);
      }
    }
  };

  const handleSave = async () => {
    if (!validateStep(step)) return;
    setSaving(true);
    try {
      const basePayload = { ...form, is_draft: false, attachments: existingKeys };
      const savedInspection = await onSave(basePayload);
      if (!savedInspection?._id) throw new Error('onSave did not return an inspection with _id');
      await processAttachments(savedInspection._id);
      toast.success('Inspection saved successfully');
      onClose();
    } catch (err) {
      console.error('Save failed:', err);
      toast.error(err.message || 'Failed to save inspection');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDraft = async () => {
    if (!String(form.unit_number || '').trim()) {
      setErrors({ unit_number: 'Unit number is required even for drafts' });
      setStep(0);
      return;
    }
    setSavingDraft(true);
    try {
      const basePayload = { ...form, is_draft: true, attachments: existingKeys };
      const savedInspection = await onSave(basePayload);
      if (!savedInspection?._id) throw new Error('onSave did not return an inspection with _id');
      await processAttachments(savedInspection._id);
      toast.success('Draft saved successfully');
      onClose();
    } catch (err) {
      console.error('Draft save failed:', err);
      toast.error(err.message || 'Failed to save draft');
    } finally {
      setSavingDraft(false);
    }
  };

  const renderStep0 = () => (
    <div>
      {isDraftRecord && <DraftBanner />}
      <SectionHeader icon="home" title="Basic Information" accent="#6366F1" />
      <Grid cols={2}>
        <Field>
          <FieldLabel required>Unit Number</FieldLabel>
          {unitOptions?.length > 0 ? (
            <Select
              options={unitOptions}
              value={unitOptions.find(o => String(o.value) === String(form.unit_number)) || null}
              onChange={o => set('unit_number', o?.value || '')}
              placeholder="Select unit…"
              isClearable
              isDisabled={mode === 'edit'}
              menuPortalTarget={document.body}
              styles={selectStyles(!!errors.unit_number)}
            />
          ) : (
            <TextInput value={form.unit_number} onChange={v => set('unit_number', v)} placeholder="e.g. 101" hasError={!!errors.unit_number} />
          )}
          <ErrorMsg msg={errors.unit_number} />
        </Field>
        <Field>
          <FieldLabel required>Inspection Date</FieldLabel>
          <TextInput type="date" value={form.inspection_date} onChange={v => set('inspection_date', v)} hasError={!!errors.inspection_date} />
          <ErrorMsg msg={errors.inspection_date} />
        </Field>
        <Field span={2}>
          <FieldLabel required>Cleanliness</FieldLabel>
          <PillToggle options={CLEANLINESS_OPTIONS} value={form.cleanliness} onChange={v => set('cleanliness', v)} />
          <ErrorMsg msg={errors.cleanliness} />
        </Field>
        <Field>
          <FieldLabel>Any Damages</FieldLabel>
          <PillToggle options={YES_NO_OPTIONS} value={form.any_damages} onChange={v => set('any_damages', v)} />
        </Field>
        <Field>
          <FieldLabel>Signs of Smoking</FieldLabel>
          <PillToggle options={YES_NO_OPTIONS} value={form.signs_of_smoking} onChange={v => set('signs_of_smoking', v)} />
        </Field>
        <Field>
          <FieldLabel required>Plumbing</FieldLabel>
          <PillToggle options={PLUMBING_OPTIONS} value={form.plumbing} onChange={v => set('plumbing', v)} />
          <ErrorMsg msg={errors.plumbing} />
        </Field>
        <Field>
          <FieldLabel>Pets</FieldLabel>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <NumberStepper value={form.number_of_dogs} onChange={v => set('number_of_dogs', v)} label="Dogs" icon="🐶" color="#8B5CF6" />
            <NumberStepper value={form.number_of_cats} onChange={v => set('number_of_cats', v)} label="Cats" icon="🐱" color="#F59E0B" />
          </div>
        </Field>
      </Grid>
    </div>
  );

  const renderStep1 = () => (
    <div>
      <SectionHeader icon={isApt ? 'wind' : 'home'} title={isApt ? 'Apartment Checks' : 'Townhouse Checks'} accent="#8B5CF6" />
      <Grid cols={2}>
        <Field>
          <FieldLabel required>Dye Test</FieldLabel>
          <PillToggle options={DYE_OPTIONS} value={form.dye_test} onChange={v => set('dye_test', v)} />
          <ErrorMsg msg={errors.dye_test} />
        </Field>
        <Field>
          <FieldLabel required>Dead Bolt</FieldLabel>
          {}
          <PillToggle options={BOLT_OPTIONS} value={form.lubricate_dead_bolt} onChange={v => set('lubricate_dead_bolt', v)} />
          <ErrorMsg msg={errors.lubricate_dead_bolt} />
        </Field>
        <Field>
          <FieldLabel required>Number of People</FieldLabel>
          <Select
            options={PEOPLE_OPTIONS}
            value={PEOPLE_OPTIONS.find(o => o.value === form.number_of_people) || null}
            onChange={o => set('number_of_people', o?.value || '')}
            placeholder="Select…"
            menuPortalTarget={document.body}
            styles={selectStyles(!!errors.number_of_people)}
          />
          <ErrorMsg msg={errors.number_of_people} />
        </Field>
        {isApt ? (
          <>
            <Field>
              <FieldLabel required>Closed Window (Winters)</FieldLabel>
              {}
              <PillToggle options={YES_NO_ONLY_OPTIONS} value={form.closed_window} onChange={v => set('closed_window', v)} />
              <ErrorMsg msg={errors.closed_window} />
            </Field>
            <Field span={2}>
              <FieldLabel required>Furniture Away from Heating Pipes</FieldLabel>
              {}
              <PillToggle options={YES_NO_ONLY_OPTIONS} value={form.furniture_away} onChange={v => set('furniture_away', v)} />
              <ErrorMsg msg={errors.furniture_away} />
            </Field>
          </>
        ) : (
          <>
            <Field>
              <FieldLabel required>HRV Setting</FieldLabel>
              <PillToggle options={HRV_OPTIONS} value={form.hrv_setting} onChange={v => set('hrv_setting', v)} />
              <ErrorMsg msg={errors.hrv_setting} />
            </Field>
            <Field>
              <FieldLabel required>Furnace Filter</FieldLabel>
              <PillToggle options={FURNACE_OPTIONS} value={form.furnace_filter} onChange={v => set('furnace_filter', v)} />
              <ErrorMsg msg={errors.furnace_filter} />
            </Field>
            <Field span={2}>
              <FieldLabel required>Flooring Damage</FieldLabel>
              {}
              <PillToggle options={YES_NO_STR_OPTIONS} value={form.flooring_damage} onChange={v => set('flooring_damage', v)} />
              <ErrorMsg msg={errors.flooring_damage} />
            </Field>
          </>
        )}
      </Grid>
    </div>
  );

  const renderStep2 = () => (
    <div>
      <SectionHeader icon="check-circle" title="Summary & Attachments" accent="#10B981" />
      <Grid cols={2}>
        <Field span={2}>
          <FieldLabel required>Overall Status</FieldLabel>
          <PillToggle options={STATUS_OPTIONS} value={form.overall_status} onChange={v => set('overall_status', v)} />
          <ErrorMsg msg={errors.overall_status} />
        </Field>
        <Field span={2}>
          <FieldLabel>Comments</FieldLabel>
          <TextareaInput value={form.comments} onChange={v => set('comments', v)} placeholder="Any additional notes about this inspection…" />
        </Field>
      </Grid>

      <div style={{ marginTop: 20 }}>
        <SectionHeader icon="paperclip" title="Attachments" accent="#F59E0B" />
        <AttachmentZone
          pendingFiles={pendingFiles}
          onPendingChange={setPendingFiles}
          existingKeys={existingKeys}
          onExistingChange={setExistingKeys}
          inspectionId={initialData?._id}
          isEdit={mode === 'edit'}
        />
      </div>
    </div>
  );

  const steps = [renderStep0, renderStep1, renderStep2];
  const isCreate = mode === 'create';

  return (
    <Modal
      isOpen={isOpen}
      toggle={handleCancel}
      size="lg"
      style={{ maxWidth: 720, width: '95%' }}
      centered
      className="si-form-modal"
    >
      {}
      <div style={{
        background: isDraftRecord
          ? 'linear-gradient(135deg, #1C1400 0%, #3D2A00 50%, #1C1400 100%)'
          : 'linear-gradient(135deg, #0F172A 0%, #1E1B4B 50%, #1E293B 100%)',
        padding: '22px 28px',
        borderRadius: '12px 12px 0 0',
        position: 'relative',
        overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', top: -30, right: -30, width: 120, height: 120, borderRadius: '50%', background: isDraftRecord ? 'rgba(217,119,6,0.18)' : 'rgba(99,102,241,0.18)', filter: 'blur(30px)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', bottom: -20, left: 80, width: 80, height: 80, borderRadius: '50%', background: isDraftRecord ? 'rgba(245,158,11,0.15)' : 'rgba(139,92,246,0.15)', filter: 'blur(20px)', pointerEvents: 'none' }} />

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{
              width: 48, height: 48, borderRadius: 14,
              background: isDraftRecord ? 'linear-gradient(135deg, rgba(217,119,6,0.3), rgba(245,158,11,0.3))' : 'linear-gradient(135deg, rgba(99,102,241,0.3), rgba(139,92,246,0.3))',
              border: isDraftRecord ? '1px solid rgba(217,119,6,0.4)' : '1px solid rgba(99,102,241,0.4)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
              <FeatherIcon icon={isDraftRecord ? 'edit-3' : isCreate ? 'plus-circle' : 'edit-2'} size={22} color={isDraftRecord ? '#FCD34D' : '#A5B4FC'} />
            </div>
            <div>
              <div style={{ fontSize: '10px', fontWeight: 700, letterSpacing: '1.2px', textTransform: 'uppercase', color: isDraftRecord ? 'rgba(252,211,77,0.7)' : 'rgba(165,180,252,0.7)', marginBottom: 3 }}>
                Suite Inspection {isDraftRecord ? '— Draft' : ''}
              </div>
              <div style={{ fontSize: '17px', fontWeight: 800, color: '#F8FAFC', letterSpacing: '-0.3px' }}>
                {isCreate ? 'New Inspection' : isDraftRecord ? `Complete Draft — Unit ${initialData?.unit_number || ''}` : `Edit — Unit ${initialData?.unit_number || ''}`}
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={handleCancel}
            style={{
              background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)',
              borderRadius: 10, width: 36, height: 36,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', transition: 'background 0.15s',
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.16)'}
            onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.08)'}
          >
            <FeatherIcon icon="x" size={16} color="white" />
          </button>
        </div>
      </div>

      <ModalBody style={{ padding: '28px 28px 0', background: '#FFFFFF', flexDirection: 'column' }}>
        <StepBar current={step} total={3} labels={STEPS} />
        <div style={{ minHeight: 320 }}>
          {steps[step]?.()}
        </div>
      </ModalBody>

      {}
      <div style={{
        padding: '20px 28px',
        background: '#FAFBFF',
        borderTop: '1px solid #F1F5F9',
        borderRadius: '0 0 12px 12px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {[0, 1, 2].map(i => (
            <div key={i} style={{
              width: i === step ? 20 : 6, height: 6, borderRadius: 3,
              background: i <= step ? '#6366F1' : '#E2E8F0',
              transition: 'all 0.3s ease',
            }} />
          ))}
          <span style={{ fontSize: '11px', color: '#94A3B8', marginLeft: 6, fontWeight: 500 }}>
            Step {step + 1} of 3
          </span>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {step > 0 && (
            <button
              type="button"
              onClick={back}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '9px 18px', borderRadius: 10,
                border: '1.5px solid #E2E8F0', background: 'white',
                color: '#64748B', fontSize: '13px', fontWeight: 600,
                cursor: 'pointer', transition: 'all 0.15s',
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = '#6366F1'; e.currentTarget.style.color = '#6366F1'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = '#E2E8F0'; e.currentTarget.style.color = '#64748B'; }}
            >
              <FeatherIcon icon="arrow-left" size={14} /> Back
            </button>
          )}

          <button
            type="button"
            onClick={handleSaveDraft}
            disabled={savingDraft || saving}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '9px 18px', borderRadius: 10,
              border: '1.5px solid #FCD34D',
              background: savingDraft ? '#FEF3C7' : 'linear-gradient(135deg, #FFFBEB, #FEF3C7)',
              color: '#92400E', fontSize: '13px', fontWeight: 700,
              cursor: savingDraft || saving ? 'not-allowed' : 'pointer',
              transition: 'all 0.15s',
              opacity: saving ? 0.5 : 1,
            }}
          >
            {savingDraft
              ? <><Spinner size="sm" style={{ width: 14, height: 14, marginRight: 4 }} /> Saving…</>
              : <><FeatherIcon icon="edit-3" size={13} /> Save as Draft</>
            }
          </button>

          {}
          <button
            type="button"
            onClick={handleCancel}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '9px 18px', borderRadius: 10,
              border: '1.5px solid #E2E8F0', background: 'white',
              color: '#94A3B8', fontSize: '13px', fontWeight: 600,
              cursor: 'pointer', transition: 'all 0.15s',
            }}
            onMouseEnter={e => e.currentTarget.style.borderColor = '#EF4444'}
            onMouseLeave={e => e.currentTarget.style.borderColor = '#E2E8F0'}
          >
            Cancel
          </button>

          {step < 2 ? (
            <button
              type="button"
              onClick={next}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '9px 22px', borderRadius: 10,
                border: 'none',
                background: 'linear-gradient(135deg, #6366F1, #8B5CF6)',
                color: 'white', fontSize: '13px', fontWeight: 700,
                cursor: 'pointer', transition: 'all 0.15s',
                boxShadow: '0 4px 14px rgba(99,102,241,0.35)',
              }}
              onMouseEnter={e => e.currentTarget.style.filter = 'brightness(1.1)'}
              onMouseLeave={e => e.currentTarget.style.filter = 'none'}
            >
              Next <FeatherIcon icon="arrow-right" size={14} />
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSave}
              disabled={saving || savingDraft}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '9px 22px', borderRadius: 10,
                border: 'none',
                background: saving ? '#E2E8F0' : 'linear-gradient(135deg, #10B981, #059669)',
                color: saving ? '#94A3B8' : 'white',
                fontSize: '13px', fontWeight: 700,
                cursor: saving || savingDraft ? 'not-allowed' : 'pointer',
                transition: 'all 0.15s',
                boxShadow: saving ? 'none' : '0 4px 14px rgba(16,185,129,0.35)',
              }}
            >
              {saving
                ? <><Spinner size="sm" style={{ width: 14, height: 14, marginRight: 4 }} /> Saving…</>
                : <><FeatherIcon icon="check" size={14} />{isCreate ? 'Submit Inspection' : isDraftRecord ? 'Submit Draft' : 'Save Changes'}</>
              }
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
};